import { useState, useCallback } from 'react';
import PropTypes from 'prop-types';
import { Box, Typography } from '@mui/material';
import { Iconify } from 'src/components';
import { outcomeButtonsContainerSx, outcomeBtnSx } from '../styles';

const defaultOptions = [
  {
    value: 'submit',
    label: 'Submit',
    iconName: 'mdi:send',
    variant: 'submit',
    description: '',
  },
  {
    value: 'not-booked',
    label: 'Spoke - Not Booked',
    iconName: 'mdi:calendar-remove',
    variant: 'not-booked',
    description: "Spoke to patient but they didn't book",
  },
  {
    value: 'booked',
    label: 'Book Appointment',
    iconName: 'mdi:check-circle',
    variant: 'booked',
    description: 'Patient wants to book',
  },
];

const OutcomeButtons = ({
  options = defaultOptions,
  selectedValue,
  onChange,
  disabled = false,
}) => {
  // Track which button was just clicked to re-trigger the bounce animation
  const [animatingValue, setAnimatingValue] = useState(null);

  const handleClick = useCallback((value) => {
    if (disabled) return;
    // Reset animation state to allow re-trigger on same button
    setAnimatingValue(null);
    requestAnimationFrame(() => {
      setAnimatingValue(value);
      onChange?.(value);
    });
  }, [disabled, onChange]);

  return (
    <Box sx={outcomeButtonsContainerSx}>
      {options.map((option) => {
        const isSelected = selectedValue === option.value;
        const isAnimating = animatingValue === option.value;

        return (
          <Box
            key={option.value}
            onClick={() => handleClick(option.value)}
            sx={{
              ...outcomeBtnSx(option.variant || 'submit', isSelected),
              // Override animation only when actively animating
              ...(isAnimating && {
                animation: 'outcomeBounce 0.2s cubic-bezier(0.34, 1.56, 0.64, 1)',
              }),
              ...(disabled && {
                opacity: 0.5,
                cursor: 'not-allowed',
                pointerEvents: 'none',
              }),
            }}
            onAnimationEnd={() => setAnimatingValue(null)}
          >
            <Iconify icon={option.iconName || 'mdi:check'} width={20} height={20} />
            <Typography component="span">{option.label}</Typography>
            {option.description && (
              <Typography component="span" sx={{ fontSize: '0.6875rem', color: '#919EAB', fontWeight: 400 }}>
                {option.description}
              </Typography>
            )}
          </Box>
        );
      })}
    </Box>
  );
};

OutcomeButtons.propTypes = {
  options: PropTypes.arrayOf(
    PropTypes.shape({
      value: PropTypes.string.isRequired,
      label: PropTypes.string.isRequired,
      iconName: PropTypes.string,
      variant: PropTypes.oneOf(['submit', 'not-booked', 'booked']),
      description: PropTypes.string,
    })
  ),
  selectedValue: PropTypes.string,
  onChange: PropTypes.func,
  disabled: PropTypes.bool,
};

export default OutcomeButtons;
